<template>
  <v-container class="px-5">
    <slot name="stats" />

    <v-card class="mt-5">
      <v-card-title>
        {{ title }}
      </v-card-title>

      <v-divider />

      <slot name="list">
        <v-container>
          <div class="text-uppercase d-flex flex-column align-center">
            <h1 class="text-h4 mb-5">Listing slot</h1>
          </div>
        </v-container>
      </slot>
    </v-card>

    <v-navigation-drawer
      v-model="drawer"
      right
      stateless
      fixed
      height="100%"
      width="600"
      temporary
      class="elevation-3 v-navigation-drawer--is-mobile"
      v-click-outside="onClickOutSide"
    >
      <v-card
        v-if="reviewLine"
        flat
        tile
        :loading="reviewLine.loading"
        :disabled="reviewLine.loading"
        height="100%"
      >
        <v-card-title>
          <v-btn icon @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <v-spacer />

          Applicant Profile
          <v-spacer />

          <v-menu left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-menu</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item-group>
                <v-list-item @click="approve(reviewLine)">
                  <v-list-item-icon>
                    <v-icon color="success">mdi-account-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ approveCaption }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hold(reviewLine)">
                  <v-list-item-icon>
                    <v-icon color="warning">mdi-hand-back-right</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>On Hold</v-list-item-title>
                </v-list-item>
                <v-list-item @click="decline(reviewLine)">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-close</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Decline</v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-card-text
          class="d-flex flex-column justify-center align-center mt-5"
        >
          <v-avatar rounded size="100" color="grey">
            <v-img :src="reviewLine.profile.picture" />
          </v-avatar>

          <v-list-item three-line dense>
            <v-list-item-content class="text-center">
              <v-list-item-title class="text-h6">
                {{ reviewLine.name }}
              </v-list-item-title>

              <v-list-item-subtitle class="overline">
                {{ reviewLine.AppliedCategory }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-card-text>

        <v-card-actions>
          <v-btn
            :disabled="!reviewLine.profile"
            @click="reviewDoc"
            small
            block
            color="primary"
          >
            <v-icon left>mdi-eye</v-icon>
            View applicant
          </v-btn>
        </v-card-actions>

        <v-simple-table dense>
          <tbody>
            <tr v-if="isLicense">
              <td>Registration Number</td>
              <td>
                {{ reviewLine.profile.MemberNo }}
              </td>
            </tr>
            <tr>
              <td>Gender</td>
              <td>{{ reviewLine.Gender ?? reviewLine.gender }}</td>
            </tr>
            <tr>
              <td>DOB</td>
              <td>
                {{
                  formatDate(
                    reviewLine.DateOfBirth ?? reviewLine.profile.DateOfBirth,
                    6
                  )
                }}
              </td>
            </tr>
            <tr>
              <td>Discipline</td>
              <td>{{ reviewLine.profile.Discipline }}</td>
            </tr>
            <tr>
              <td>Phone</td>
              <td>{{ reviewLine.PhoneNo ?? reviewLine.practitionerPhone }}</td>
            </tr>
            <tr>
              <td>Email</td>
              <td>{{ reviewLine.Email ?? reviewLine.practitionerEmail }}</td>
            </tr>
            <tr v-if="isLicense">
              <td>Good standing status</td>
              <td>
                {{ reviewLine.profile.memberInformation?.at(0)?.goodStanding }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="mt-3" />

        <v-divider class="mt-3" />
        <v-card :key="randomHint" v-if="isLicense">
          <v-card-text>
            <h6>Subscriptions</h6>
            <v-data-table
              :headers="subscriptionHeaders"
              :items="reviewLine.profile.subscriptions"
              class="elevation-1 mt-10"
            >
              <template v-slot:[`item.memberCategory`]="{ item }">
                {{ item.memberCategories.Description }}
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="item.status === 'Active' ? 'primary' : 'error'"
                  >{{ item.status }}</v-chip
                >
              </template>
              <template v-slot:[`item.paidAmount`]="{ item }">
                {{ intlFormatNumber(item.paidAmount) }}
              </template>
              <template v-slot:[`item.billedAmount`]="{ item }">
                {{ intlFormatNumber(item.billedAmount) }}
              </template>
              <template v-slot:[`item.fullyPaid`]="{ item }">
                <v-chip :color="item.fullyPaid ? 'success' : 'warning'">{{
                  item.fullyPaid ? "Yes" : "No"
                }}</v-chip>
              </template>
              <template v-slot:[`item.lastPaidDate`]="{ item }">
                {{ formatDate(item.lastPaidDate, 8) }}
              </template>
              <template v-slot:[`item.attachment`]="{ item }">
                <v-btn
                  small
                  color="secondary"
                  outlined
                  @click="getPaymentReceipt(item)"
                >
                  <v-icon left>mdi-file</v-icon>
                  View
                </v-btn>
              </template>
            </v-data-table>
            <v-divider class="mt-4" />
            <h6>CPD Points</h6>
            <v-data-table
              :headers="cpdHeaders"
              :items="reviewLine.profile.cpds"
              class="elevation-1 mt-10"
            >
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-tabs v-model="tab" grow class="mt-5">
          <v-tab>
            <v-icon left>mdi-comment</v-icon>
            Comments
          </v-tab>
          <v-tab>
            <v-icon left>mdi-timeline-clock</v-icon>
            History
          </v-tab>

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-list three-line flat elevation="0">
                <v-list-item-group active-class="primary--text">
                  <template
                    v-for="(comment, index) in reviewLine.comments || []"
                  >
                    <v-list-item
                      :key="`comment-${index}`"
                      v-if="comment.comment !== ''"
                    >
                      <template v-slot:default>
                        <v-list-item-content>
                          <v-list-item-title
                            class="overline"
                            v-text="comment.displayName"
                          />

                          <v-list-item-subtitle
                            class="caption"
                            v-text="comment.comment"
                          />
                        </v-list-item-content>

                        <v-list-item-action>
                          <v-list-item-action-text
                            v-text="formatDate(comment.commentDate, 6)"
                          />
                        </v-list-item-action>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index < reviewLine.comments?.length - 1"
                      :key="index"
                    />
                  </template>

                  <v-list-item v-if="reviewLine.comments?.length === 0">
                    <v-list-item-content>
                      <v-list-item-title class="font-italic">
                        No Comments
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-tab-item>

            <v-tab-item>
              <v-list three-line flat elevation="0">
                <v-list-item-group active-class="primary--text">
                  <template v-for="(log, index) in reviewLine.logs">
                    <v-list-item :key="`log-${index}`">
                      <template v-slot:default>
                        <v-list-item-content>
                          <v-list-item-title
                            v-if="log.reviewer"
                            class="overline"
                            v-text="log ? log.reviewer.names : ''"
                          />

                          <v-list-item-subtitle
                            class="caption"
                            v-text="'From : ' + log.fromStage"
                          />
                          <v-list-item-subtitle
                            class="caption"
                            v-text="'To : ' + log.toStage"
                          />
                        </v-list-item-content>

                        <v-list-item-action>
                          <v-list-item-action-text
                            v-text="formatDate(log.systemCreatedAt, 6)"
                          />
                        </v-list-item-action>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index < reviewLine.reviewLogs?.length - 1"
                      :key="index"
                    />
                  </template>

                  <v-list-item v-if="reviewLine.reviewLogs?.length === 0">
                    <v-list-item-content>
                      <v-list-item-title class="font-italic">
                        No History
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </v-card>
    </v-navigation-drawer>
    <reviewStageChecklistDialogVue />
  </v-container>
</template>

<script>
import { DateMixin } from "@/mixins";
import NullCheckMixin from "@/mixins/nullCheckMixin";
import EventBus from "@/utils/eventBus";
import approvalMixin from "@/modules/review/approvalMixin";
import Application from "@/modules/application/models/application";
import reviewStageChecklistDialogVue from "./reviewStageChecklistDialog.vue";

export default {
  name: "reviewListing",
  components: { reviewStageChecklistDialogVue },
  mixins: [DateMixin, NullCheckMixin, approvalMixin],
  props: {
    title: {
      type: String,
      default: "Review",
    },
    reviewLine: {
      type: Object,
      default: () => new Application({}),
    },
    type: {
      type: String,
      default: "application",
    },
  },
  data: () => ({
    drawer: false,
    tab: null,
    randomHint: 0,
    subscriptionHint: 0,
    subscriptionHeaders: [
      {
        text: "Period",
        align: "start",
        sortable: false,
        value: "subsPeriod",
      },
      { text: "Category", value: "memberCategory", sortable: false },
      { text: "Status", value: "status", sortable: false },
      { text: "Amount Paid", value: "paidAmount", sortable: false },
      { text: "Balance", value: "billedAmount", sortable: false },
      { text: "Fully Paid", value: "fullyPaid", sortable: false },
      { text: "Date Paid", value: "lastPaidDate", sortable: false },
      { text: "Receipts", value: "attachment", sortable: false },
    ],
    cpdHeaders: [
      {
        text: "Period",
        align: "start",
        sortable: false,
        value: "period",
      },
      { text: "CPD Points", value: "cpd", sortable: false },
      { text: "Target", value: "target", sortable: false },
    ],
  }),

  mounted() {
    EventBus.$on("item-selected", () => {
      setTimeout(() => {
        this.drawer = true;
      }, 200);
    });

    setInterval(() => {
      this.randomHint = Math.random();
      this.subscriptionHint = Math.random();
    }, 100);
  },
  computed: {
    isLicense() {
      return this.$route.fullPath.includes("license");
    },
  },
  methods: {
    intlFormatNumber(val, currency = "KES", locale = "en-KE") {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
      }).format(val);
    },
    reviewDoc: function () {
      this.$router.push({
        name: "Review Card",
        params: {
          Code: this.reviewLine.Code,
        },
        query: {
          type: this.type,
        },
      });
    },

    onClickOutSide: function () {
      if (!this.drawer) return;

      setTimeout(() => {
        if (this.drawer) this.drawer = false;
      }, 200);
    },
  },
};
</script>

<style scoped>
.v-tabs--icons-and-text > .v-tabs-bar {
  height: 0;
}
</style>
